import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../app/hooks';
import { selectFeatureFlagPosCart } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import { OrderActions } from '../OrderReview/OrderActions';
import AgentCartCard from './AgentCartCard';
import OrderTotalCard from './OrderTotalCard';
import PosCartCard from './PosCartCard';
import useRightStackCollapse from './hooks/useRightStackCollapse.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Right pane wrapper (mirrors LeftSidebar's leftCard): a flush column holding
  // the Agent Cart / POS Cart stack + the pinned totals / Cancel-Finish footer.
  // No outer frame or padding — sections attach edge-to-edge.
  outer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Holds the resizable card stack; the footer sits below it (pinned).
  panelRegion: {
    flex: 1,
    minHeight: 0,
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works; overflow hidden (not auto) so no outer
  // scrollbar appears beside a collapsed hug-height card.
  stackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Hairline divider between the Agent Cart / POS Cart cards (mirrors
  // LeftSidebar's vResizeHandle): a full-width 1px rule with a centred grip pill
  // that grows + turns blue on hover/drag.
  vResizeHandle: {
    position: 'relative',
    height: spacing(1.25),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    backgroundColor: Colors.white,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: Colors.alto,
    },
    '&::after': {
      content: '""',
      zIndex: 1,
      height: 3,
      width: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, width 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      width: spacing(5),
    },
  },
  // Pinned footer: subtotal / tax / total + Cancel / Finish. Sits BELOW the
  // resizable stack so it stays visible however the cards are resized or
  // collapsed — the whole point of splitting the carts into their own panels.
  // Flush like the sections above: attached by a single top rule, no card frame.
  footer: {
    flexShrink: 0,
    backgroundColor: Colors.white,
    borderTop: `1px solid ${Colors.alto}`,
    overflow: 'hidden',
  },
}));

interface RightSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
}

/**
 * RIGHT SIDEBAR of the HITL console. A drag-to-resizable vertical stack of the
 * Agent Cart and POS Cart cards (sizes persist per agent via autoSaveId, the
 * vertical twin of LeftSidebar), with the order totals + Cancel / Finish actions
 * pinned in a footer below the stack so they never scroll away. The POS Cart
 * card is present only when the pos_cart flag is on. Collapse-sync between each
 * card's chevron and its panel lives in useRightStackCollapse.
 */
const RightSidebar = ({ paneStorageKey }: RightSidebarProps) => {
  const classes = useStyles();
  const isPosCartEnabled = useAppSelector(selectFeatureFlagPosCart);
  const {
    agentOpen,
    setAgentOpen,
    posOpen,
    setPosOpen,
    agentPanelRef,
    posPanelRef,
  } = useRightStackCollapse();

  return (
    <div className={classes.outer}>
      <div className={classes.panelRegion}>
        <PanelGroup
          direction="vertical"
          autoSaveId={`${paneStorageKey}:right-stack`}
        >
          <Panel
            id="agent-cart"
            order={1}
            ref={agentPanelRef}
            collapsible
            collapsedSize={8}
            defaultSize={isPosCartEnabled ? 80 : 100}
            minSize={22}
            onCollapse={() => setAgentOpen(false)}
            onExpand={() => setAgentOpen(true)}
          >
            <div className={classes.stackPane}>
              <AgentCartCard open={agentOpen} onOpenChange={setAgentOpen} />
            </div>
          </Panel>

          {isPosCartEnabled && (
            <>
              <PanelResizeHandle className={classes.vResizeHandle} />
              <Panel
                id="pos-cart"
                order={2}
                ref={posPanelRef}
                collapsible
                collapsedSize={8}
                defaultSize={20}
                minSize={15}
                onCollapse={() => setPosOpen(false)}
                onExpand={() => setPosOpen(true)}
              >
                <div className={classes.stackPane}>
                  <PosCartCard open={posOpen} onOpenChange={setPosOpen} />
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>

      {/* Pinned footer — totals + Cancel / Finish, always visible. */}
      <div className={classes.footer}>
        <OrderTotalCard />
        <OrderActions />
      </div>
    </div>
  );
};

export default RightSidebar;
